/**
 * Utility functions per la gestione e ordinamento dei ruoli
 */

/**
 * Calcola lo score di un ruolo per l'ordinamento
 * Score più basso = priorità più alta
 * 
 * @param {Object} role - Il ruolo da valutare
 * @param {Boolean} role.is_contract_manager - Se il ruolo è relativo al contract manager
 * @param {Boolean} role.is_highlighted - Se il ruolo è evidenziato
 * @param {Boolean} isAssigned - Se il ruolo è già assegnato (opzionale)
 * @returns {Number} Score del ruolo (1-8)
 */
function calculateRoleScore(role, isAssigned = false) {
  const isContractManager = role.is_contract_manager || false
  const isHighlighted = role.is_highlighted || false
  
  if (isAssigned) {
    // Ruoli assegnati (priorità 1-4)
    if (isContractManager && isHighlighted) return 1
    if (isContractManager && !isHighlighted) return 2
    if (!isContractManager && isHighlighted) return 3
    if (!isContractManager && !isHighlighted) return 4
  } else {
    // Ruoli non assegnati (priorità 5-8)
    if (isContractManager && isHighlighted) return 5
    if (isContractManager && !isHighlighted) return 6
    if (!isContractManager && isHighlighted) return 7
    if (!isContractManager && !isHighlighted) return 8
  }
  
  // Default (non dovrebbe mai arrivarci)
  return 9
}

/**
 * Ordina una lista di ruoli secondo i criteri di priorità
 * 
 * Ordine di priorità:
 * 1. Ruoli assegnati relativi al contract manager highlighted
 * 2. Ruoli assegnati relativi al contract manager non highlighted
 * 3. Ruoli assegnati globali highlighted
 * 4. Ruoli assegnati globali non highlighted
 * 5. Ruoli non assegnati relativi al contract manager highlighted
 * 6. Ruoli non assegnati relativi al contract manager non highlighted
 * 7. Ruoli non assegnati globali highlighted
 * 8. Ruoli non assegnati globali non highlighted
 * 
 * @param {Array} roles - Lista dei ruoli da ordinare
 * @param {Array} assignedRoleIds - Lista degli ID dei ruoli già assegnati (opzionale)
 * @returns {Array} Ruoli ordinati
 */
export function sortRolesByPriority(roles, assignedRoleIds = []) {
  if (!Array.isArray(roles)) return []
  
  // Converti assignedRoleIds in un Set per lookup più efficiente
  const assignedSet = new Set(
    assignedRoleIds.map(id => typeof id === 'object' ? id.id : id)
  )
  
  return [...roles].sort((a, b) => {
    // Determina se i ruoli sono assegnati
    const aIsAssigned = assignedSet.has(a.id)
    const bIsAssigned = assignedSet.has(b.id)
    
    // Calcola gli score
    const scoreA = calculateRoleScore(a, aIsAssigned)
    const scoreB = calculateRoleScore(b, bIsAssigned)
    
    // Ordina per score
    if (scoreA !== scoreB) {
      return scoreA - scoreB
    }
    
    // A parità di score, ordina alfabeticamente per nome
    return (a.name || '').localeCompare(b.name || '')
  })
}

/**
 * Filtra e ordina i ruoli per la visualizzazione
 * 
 * @param {Array} availableRoles - Ruoli disponibili
 * @param {Array} currentRoles - Ruoli attualmente assegnati
 * @param {Object} options - Opzioni aggiuntive
 * @param {Number} options.limit - Numero massimo di ruoli da mostrare
 * @param {Boolean} options.includeNonEditable - Include ruoli non editabili
 * @returns {Object} Oggetto con ruoli ordinati e informazioni aggiuntive
 */
export function prepareRolesForDisplay(availableRoles, currentRoles = [], options = {}) {
  const {
    limit = null,
    includeNonEditable = true
  } = options
  
  // Crea una mappa di tutti i ruoli
  const roleMap = new Map()
  
  // Aggiungi i ruoli attualmente assegnati
  currentRoles.forEach(role => {
    if (!includeNonEditable && role.is_editable === false) return
    roleMap.set(role.id, {
      ...role,
      isAssigned: true
    })
  })
  
  // Aggiungi i ruoli disponibili
  availableRoles.forEach(role => {
    if (!roleMap.has(role.id)) {
      roleMap.set(role.id, {
        ...role,
        isAssigned: false
      })
    } else {
      // Merge delle informazioni se il ruolo esiste in entrambe le liste
      const existing = roleMap.get(role.id)
      roleMap.set(role.id, {
        ...existing,
        is_highlighted: role.is_highlighted !== undefined ? role.is_highlighted : existing.is_highlighted,
        is_contract_manager: role.is_contract_manager !== undefined ? role.is_contract_manager : existing.is_contract_manager
      })
    }
  })
  
  // Converti in array e ordina
  const allRoles = Array.from(roleMap.values())
  const assignedIds = currentRoles.map(r => r.id)
  const sortedRoles = sortRolesByPriority(allRoles, assignedIds)
  
  // Applica il limite se specificato
  const displayedRoles = limit ? sortedRoles.slice(0, limit) : sortedRoles
  const hasMore = limit && sortedRoles.length > limit
  const remainingCount = hasMore ? sortedRoles.length - limit : 0
  
  return {
    allRoles: sortedRoles,
    displayedRoles,
    hasMore,
    remainingCount,
    total: sortedRoles.length
  }
}

export default {
  sortRolesByPriority,
  prepareRolesForDisplay
}